import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import FooterSection from '../homepage/components/FooterSection';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import FeaturedBrandSection from '../../components/FeaturedBrandSection';
import ComparisonModal from '../../components/ComparisonModal';
import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';


const SolarPanelCategoryPage = () => {
  const [comparisonProducts, setComparisonProducts] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  const handleAddToCompare = (product) => {
    setComparisonProducts((prev) => {
      const exists = prev?.find(p => p?.id === product?.id);
      if (exists) {
        return prev;
      }
      if (prev?.length >= 3) {
        return [...prev?.slice(1), product];
      }
      return [...prev, product];
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const featuredBrandData = {
    brandName: "SolarEdge",
    brandLogo: "https://ui-avatars.com/api/?name=SolarEdge&background=f59e0b&color=fff&size=80",
    brandDescription: "Wereldleider in slimme zonne-energie oplossingen",
    productName: "SolarEdge HD-Wave Omvormer + Optimizer",
    productImage: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&h=600&fit=crop",
    productImageAlt: "SolarEdge HD-Wave omvormer met power optimizers geïnstalleerd met zonnepanelen op dak voor maximale opbrengst",
    productDescription: "Innovatieve omvormer met module-level monitoring en optimizers voor maximale opbrengst per paneel. Ideaal voor daken met schaduw.",
    productSpecs: [
      { icon: "Zap", label: "Vermogen", value: "5 kW" },
      { icon: "TrendingUp", label: "Efficiëntie", value: "99.2%" },
      { icon: "Wifi", label: "Monitoring", value: "Real-time" },
      { icon: "Shield", label: "Garantie", value: "12 jaar" }
    ],
    productPrice: "€8.500",
    rating: 4.9,
    reviewCount: 456,
    reviews: [
      {
        rating: 5,
        text: "Fantastisch systeem! Zelfs bij gedeeltelijke schaduw maximale opbrengst. De app geeft perfect inzicht in productie per paneel.",
        name: "Peter van Dijk",
        location: "Utrecht",
        avatar: "https://ui-avatars.com/api/?name=Peter+vD&background=f59e0b&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "Onze energierekening is met 80% gedaald! De optimizers maken echt het verschil. Zeer tevreden met de investering.",
        name: "Sophie Janssen",
        location: "Rotterdam",
        avatar: "https://ui-avatars.com/api/?name=Sophie+J&background=ea580c&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "Professionele installatie en topkwaliteit. De monitoring app is gebruiksvriendelijk en zeer informatief. Aanrader!",
        name: "Mark de Vries",
        location: "Amsterdam",
        avatar: "https://ui-avatars.com/api/?name=Mark+dV&background=dc2626&color=fff",
        verified: true
      }
    ],
    colorScheme: "from-yellow-500/10 to-orange-500/10",
    accentColor: "text-yellow-600",
    bgGradient: "from-yellow-50 via-orange-50 to-amber-50"
  };

  const solarTypes = [
    {
      id: 1,
      name: "Monokristallijne panelen",
      description: "Hoogste rendement en langste levensduur. Ideaal voor beperkte dakoppervlakte.",
      suitability: "Geschikt voor woningen met beperkt dakoppervlak en hoge energiebehoefte",
      attention: "Hogere aanschafprijs, maar beste prijs-kwaliteit op lange termijn",
      efficiency: "19-22%",
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=300&fit=crop",
      alt: "Close-up van zwarte monokristallijne zonnepanelen op dak met hoge efficiëntie",
      icon: "Zap",
      color: "from-blue-500/10 to-cyan-500/10"
    },
    {
      id: 2,
      name: "Polykristallijne panelen",
      description: "Goede prijs-kwaliteitverhouding met betrouwbare prestaties.",
      suitability: "Geschikt voor standaard woningen met voldoende dakoppervlak",
      attention: "Iets lager rendement dan monokristallijn, maar betaalbaarder",
      efficiency: "15-17%",
      image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=400&h=300&fit=crop",
      alt: "Blauwe polykristallijne zonnepanelen geïnstalleerd op hellend dak",
      icon: "Sun",
      color: "from-yellow-500/10 to-orange-500/10"
    },
    {
      id: 3,
      name: "Dunne film panelen",
      description: "Flexibel en lichtgewicht, geschikt voor speciale toepassingen.",
      suitability: "Geschikt voor platte daken of gebogen oppervlakken",
      attention: "Lager rendement, meer oppervlakte nodig voor zelfde opbrengst",
      efficiency: "10-12%",
      image: "https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=400&h=300&fit=crop",
      alt: "Dunne film zonnepanelen op plat dak met moderne installatie",
      icon: "Layers",
      color: "from-purple-500/10 to-pink-500/10"
    }
  ];

  const costData = [
    { category: 'Aanschaf', amount: 6500 },
    { category: 'Installatie', amount: 1500 },
    { category: 'Subsidie', amount: -1200 },
    { category: 'Netto investering', amount: 6800 }
  ];

  const savingsData = [
    { year: 'Jaar 1', besparing: 850, cumulatief: 850 },
    { year: 'Jaar 3', besparing: 900, cumulatief: 2650 },
    { year: 'Jaar 5', besparing: 950, cumulatief: 4550 },
    { year: 'Jaar 7', besparing: 1000, cumulatief: 6550 },
    { year: 'Jaar 10', besparing: 1100, cumulatief: 9850 }
  ];

  const commonMistakes = [
    {
      id: 1,
      mistake: "Verkeerde dakoriëntatie negeren",
      correct: "Zuid-oriëntatie levert 100% rendement, oost/west 80-85%",
      icon: "AlertTriangle",
      color: "text-warning"
    },
    {
      id: 2,
      mistake: "Schaduw niet meenemen in berekening",
      correct: "Zelfs kleine schaduw kan opbrengst met 20-30% verminderen",
      icon: "Cloud",
      color: "text-error"
    },
    {
      id: 3,
      mistake: "Alleen op prijs selecteren",
      correct: "Kwaliteit panelen en garantie zijn cruciaal voor lange termijn rendement",
      icon: "DollarSign",
      color: "text-warning"
    },
    {
      id: 4,
      mistake: "Salderingsregeling niet begrijpen",
      correct: "Saldering wordt afgebouwd - combineer met thuisbatterij voor optimaal rendement",
      icon: "TrendingDown",
      color: "text-error"
    }
  ];

  const providers = [
    {
      id: 1,
      name: "SolarTech Nederland",
      rating: 4.8,
      reviews: 342,
      specialization: "Monokristallijne systemen",
      region: "Landelijk",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=100&h=100&fit=crop",
      alt: "SolarTech Nederland kantoor met zonnepanelen op dak"
    },
    {
      id: 2,
      name: "Groene Energie Installaties",
      rating: 4.7,
      reviews: 289,
      specialization: "Complete duurzame oplossingen",
      region: "Randstad",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop",
      alt: "Groene Energie Installaties bedrijfspand met moderne architectuur"
    },
    {
      id: 3,
      name: "ZonnePartner",
      rating: 4.9,
      reviews: 421,
      specialization: "Maatwerk zonne-oplossingen",
      region: "Landelijk",
      image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop",
      alt: "ZonnePartner installatieteam aan het werk op dak"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Main Content with proper spacing for fixed navigation */}
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
      <FeaturedBrandSection {...featuredBrandData} onAddToCompare={handleAddToCompare} />
      {/* Hero Section */}
      <motion.section 
        className="relative bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 overflow-hidden py-16 lg:py-24"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div variants={itemVariants}>
              <div className="inline-flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full mb-6">
                <Icon name="Sun" size={20} className="text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Zonne-energie</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Zonnepanelen: duurzaam verwarmen met toekomstvisie
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Wek uw eigen stroom op en verlaag uw energieverbruik drastisch. Goede zonnepanelen verlagen uw energierekening direct, verhogen de waarde van uw woning en maken u minder afhankelijk van energieleveranciers.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/quote-request-form">
                  <Button size="lg" iconName="FileText" iconPosition="left">
                    Offerte aanvragen
                  </Button>
                </Link>
                <Button variant="outline" size="lg" iconName="Calculator" iconPosition="left">
                  Bereken opbrengst
                </Button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-elevation-3">
                <Image
                  src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&h=600&fit=crop"
                  alt="Moderne woning met zonnepanelen op dak met energiestromen visualisatie"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Gemiddelde opbrengst</p>
                        <p className="text-2xl font-bold text-success">3.500 kWh/jaar</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Besparing</p>
                        <p className="text-2xl font-bold text-primary">€850/jaar</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      {/* Why Solar is Crucial */}
      <motion.section 
        className="py-16 lg:py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Waarom zonnepanelen cruciaal zijn
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Investeren in zonnepanelen levert directe en langdurige voordelen op
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center mb-4">
                <Icon name="TrendingDown" size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                Tot €1.500 besparing per jaar
              </h3>
              <p className="text-muted-foreground mb-4">
                Direct lagere energierekening door eigen stroomopwekking. Bij gemiddeld verbruik terugverdientijd van 7-9 jaar.
              </p>
              <div className="relative rounded-lg overflow-hidden">
                <Image
                  src="https://images.unsplash.com/photo-1554224311-beee2091d292?w=400&h=250&fit=crop"
                  alt="Energierekening met calculator toont besparing door zonnepanelen"
                  className="w-full h-48 object-cover"
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                <Icon name="Home" size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                Hogere woningwaarde
              </h3>
              <p className="text-muted-foreground mb-4">
                Woningen met zonnepanelen hebben een hoger energielabel en zijn aantrekkelijker voor kopers.
              </p>
              <div className="relative rounded-lg overflow-hidden">
                <Image
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=250&fit=crop"
                  alt="Moderne woning met zonnepanelen verhoogt woningwaarde en energielabel"
                  className="w-full h-48 object-cover"
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-100">
              <div className="w-12 h-12 bg-warning rounded-lg flex items-center justify-center mb-4">
                <Icon name="Zap" size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                Energieonafhankelijkheid
              </h3>
              <p className="text-muted-foreground mb-4">
                Minder afhankelijk van stijgende energieprijzen en netcongestie. Combineer met thuisbatterij voor maximale onafhankelijkheid.
              </p>
              <div className="relative rounded-lg overflow-hidden">
                <Image
                  src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=250&fit=crop"
                  alt="Zonnepanelen op dak met batterijopslag systeem voor energieonafhankelijkheid"
                  className="w-full h-48 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      {/* Types of Solar Panels */}
      <motion.section 
        className="py-16 lg:py-20 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Soorten zonnepanelen
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Kies het type dat het beste bij uw situatie past
            </p>
          </motion.div>

          <div className="space-y-8">
            {solarTypes?.map((type, index) => (
              <motion.div
                key={type?.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-shadow"
              >
                <div className={`grid md:grid-cols-2 gap-6 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`relative ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <Image
                      src={type?.image}
                      alt={type?.alt}
                      className="w-full h-full object-cover min-h-[300px]"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${type?.color} opacity-20`}></div>
                    <div className="absolute top-4 right-4 bg-white rounded-full p-3 shadow-elevation-2">
                      <Icon name={type?.icon} size={24} className="text-primary" />
                    </div>
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2">
                      <p className="text-sm text-muted-foreground">Rendement</p>
                      <p className="text-xl font-bold text-success">{type?.efficiency}</p>
                    </div>
                  </div>

                  <div className="p-6 lg:p-8 flex flex-col justify-center">
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-3">
                      {type?.name}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {type?.description}
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="CheckCircle" size={16} className="text-success" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Wanneer geschikt</p>
                          <p className="text-sm text-muted-foreground">{type?.suitability}</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="AlertCircle" size={16} className="text-warning" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Aandachtspunten</p>
                          <p className="text-sm text-muted-foreground">{type?.attention}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      {/* Costs, ROI & Subsidies */}
      <motion.section 
        className="py-16 lg:py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Kosten, rendement & subsidies
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Inzicht in de investering en terugverdientijd
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 lg:p-8 border border-blue-100">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                Kostenoverzicht (8 panelen, 3.500 Wp)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={costData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value) => `€${value?.toLocaleString('nl-NL')}`}
                  />
                  <Bar dataKey="amount" fill="#1e40af" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 lg:p-8 border border-green-100">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                Cumulatieve besparing over tijd
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={savingsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value) => `€${value?.toLocaleString('nl-NL')}`}
                  />
                  <Line type="monotone" dataKey="cumulatief" stroke="#16a34a" strokeWidth={3} dot={{ fill: '#16a34a', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Euro" size={20} className="text-primary" />
                </div>
                <h4 className="font-heading font-semibold text-foreground">Investering</h4>
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">€6.800</p>
              <p className="text-sm text-muted-foreground">Netto na subsidie (8 panelen)</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={20} className="text-success" />
                </div>
                <h4 className="font-heading font-semibold text-foreground">Terugverdientijd</h4>
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">7-9 jaar</p>
              <p className="text-sm text-muted-foreground">Bij gemiddeld verbruik</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Gift" size={20} className="text-warning" />
                </div>
                <h4 className="font-heading font-semibold text-foreground">Subsidie</h4>
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">Tot €1.200</p>
              <p className="text-sm text-muted-foreground">ISDE en lokale regelingen</p>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={24} className="text-primary flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-heading font-semibold text-foreground mb-2">Subsidies & regelingen</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span><strong>ISDE subsidie:</strong> Tot €1.200 voor zonnepanelen in combinatie met andere maatregelen</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span><strong>Salderingsregeling:</strong> Wordt afgebouwd tot 2031 - nu nog maximaal profiteren</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span><strong>Lokale subsidies:</strong> Veel gemeenten bieden extra financiële ondersteuning</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span><strong>BTW-teruggave:</strong> Mogelijk om 21% BTW terug te vragen bij aanschaf</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
      {/* Common Mistakes */}
      <motion.section 
        className="py-16 lg:py-20 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Veelgemaakte fouten
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Vermijd deze valkuilen bij de aanschaf van zonnepanelen
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {commonMistakes?.map((item) => (
              <motion.div
                key={item?.id}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 border-l-4 border-error shadow-elevation-1 hover:shadow-elevation-2 transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={item?.icon} size={24} className={item?.color} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="inline-flex items-center space-x-2 bg-error/10 px-3 py-1 rounded-full mb-2">
                          <Icon name="X" size={14} className="text-error" />
                          <span className="text-xs font-medium text-error">Fout</span>
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-foreground">
                          {item?.mistake}
                        </h3>
                      </div>
                    </div>
                    <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-success mb-1">Juiste aanpak</p>
                          <p className="text-sm text-foreground">{item?.correct}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="mt-8 relative rounded-xl overflow-hidden">
            <Image
              src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&h=400&fit=crop"
              alt="Professionele installatie van zonnepanelen met correcte oriëntatie en schaduwanalyse"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-center">
              <div className="px-8 max-w-2xl">
                <h3 className="text-2xl font-heading font-bold text-white mb-2">
                  Professioneel advies is essentieel
                </h3>
                <p className="text-white/90 mb-4">
                  Laat een gecertificeerde installateur een dakopname en schaduwanalyse uitvoeren voor een realistische opbrengstberekening.
                </p>
                <Button variant="outline" size="lg" iconName="Users" iconPosition="left" className="bg-white text-foreground hover:bg-white/90">
                  Vind installateurs
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
      {/* Providers & Reviews */}
      <motion.section 
        className="py-16 lg:py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Aanbieders & ervaringen
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Geselecteerde installateurs met bewezen kwaliteit
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {providers?.map((provider) => (
              <motion.div
                key={provider?.id}
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="bg-card border border-border rounded-xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-3 transition-all"
              >
                <div className="relative h-32 bg-gradient-to-br from-yellow-100 to-orange-100">
                  <Image
                    src={provider?.image}
                    alt={provider?.alt}
                    className="w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                    {provider?.name}
                  </h3>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex items-center">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon 
                          key={i} 
                          name="Star" 
                          size={14} 
                          className={`${i < Math.floor(provider?.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-foreground">{provider?.rating}</span>
                    <span className="text-sm text-muted-foreground">({provider?.reviews} reviews)</span>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="Wrench" size={14} className="text-primary" />
                      <span>{provider?.specialization}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="MapPin" size={14} className="text-primary" />
                      <span>{provider?.region}</span>
                    </div>
                  </div>
                  <Link to="/leveranciers">
                    <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right" fullWidth>
                      Bekijk profiel
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="text-center">
            <Link to="/quote-request-form">
              <Button size="lg" iconName="FileText" iconPosition="left">
                Ontvang offertes van meerdere installateurs
              </Button>
            </Link>
          </motion.div>
        </div>
      </motion.section>
      <FooterSection />
      </main>
      <FooterSection />
      {/* Floating Compare Button */}
      <AnimatePresence>
        {comparisonProducts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-6 right-6 z-40"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsComparisonOpen(true)}
              className="bg-primary text-white px-6 py-4 rounded-full shadow-2xl flex items-center space-x-3 hover:bg-primary/90 transition-colors"
            >
              <Icon name="GitCompare" size={24} />
              <span className="font-semibold">Vergelijk ({comparisonProducts?.length})</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        products={comparisonProducts}
        category="zonnepanelen"
      />
    </div>
  );
};

export default SolarPanelCategoryPage;